import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import {
  Button,
  DatePicker,
  Input,
  Label,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { VoucherValidationSchema } from "@/shared";
import { CreateVoucherRequestDTO } from "@/types";
import { useCreateVoucherMutation, useToast } from "@/hooks";

import { MessageError } from "../../message-error";
import { DisplayError, formatDateToBackend } from "@/utils";

type FormCreateVoucherProps = {
  handleClose: () => void;
};

export function FormCreateVoucher({ handleClose }: FormCreateVoucherProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const { mutateAsync, isLoading } = useCreateVoucherMutation();

  const initialValues = {
    code: "",
    type: "",
    value: "",
    expiration: "",
  };

  const { handleSubmit, ...formik } = useFormik({
    initialValues,
    validationSchema: VoucherValidationSchema(),
    onSubmit: async (values: CreateVoucherRequestDTO, { resetForm }) => {
      const request = {
        ...values,
        expiration: formatDateToBackend(values.expiration),
      };
      mutateAsync(request, {
        onSuccess: () => {
          handleClose();
          resetForm();
          toast({
            title: t("LABEL.success"),
            description: t("MESSAGE.created successfully"),
          });
        },
        onError: (error) => {
          toast({
            title: t("LABEL.error"),
            description: DisplayError(error, t("MESSAGE.error_message")),
            variant: "destructive",
          });
        },
      });
    },
  });
  return (
    <form
      onSubmit={handleSubmit}
      className="max-h-[50vh] md:max-h-[70vh] 2xl:max-h-[90vh] flex flex-col gap-2 pb-6"
    >
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label required>{t("LABEL.code")}</Label>
          <Input
            isInvalid={formik.touched.code && !!formik.errors.code}
            isValid={formik.touched.code && !formik.errors.code}
            messageError={formik.errors.code as string}
            {...formik.getFieldProps("code")}
          />
        </div>

        <div className="space-y-1">
          <Label required>{t("LABEL.type")}</Label>
          <div className="space-y-1">
            <Select
              {...formik.getFieldProps("type")}
              onValueChange={(value) => {
                formik.setFieldValue("type", value);
              }}
            >
              <SelectTrigger
                isInvalid={formik.touched.type && !!formik.errors.type}
                isValid={formik.touched.type && !formik.errors.type}
                className="disabled:opacity-100"
              >
                <SelectValue placeholder="Selecione o tipo de voucher" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="percentage">
                    {t("LABEL.percentage")}
                  </SelectItem>
                  <SelectItem value="fixed">{t("LABEL.fixed")}</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>

            <MessageError
              isInvalid={formik.touched.type && !!formik.errors.type}
              messageError={formik.errors.type as string}
            />
          </div>
        </div>

        <div className="space-y-1">
          <Label required>{t("LABEL.value")}</Label>
          <Input
            inputMoney={formik.values.type === "fixed"}
            inputPercentage={formik.values.type === "percentage"}
            isInvalid={formik.touched.value && !!formik.errors.value}
            isValid={formik.touched.value && !formik.errors.value}
            messageError={formik.errors.value as string}
            {...formik.getFieldProps("value")}
          />
        </div>
        <div className="space-y-1">
          <Label required>{t("LABEL.expiration")}</Label>
          <DatePicker
            {...formik.getFieldProps("expiration")}
            isInvalid={formik.touched.expiration && !!formik.errors.expiration}
            isValid={formik.touched.expiration && !formik.errors.expiration}
            messageError={formik.errors.expiration}
          />
        </div>
      </div>
      <div className="flex items-center justify-center gap-2 mt-2">
        <Button isLoading={isLoading} className="px-10">
          {t("LABEL.save")}
        </Button>
      </div>
    </form>
  );
}
